import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { translations, type Language, type TranslationKey } from '../i18n';

export type ThemePreference = 'light' | 'dark' | 'system';
export type ResolvedTheme = 'light' | 'dark';

interface UISettingsContextType {
  language: Language;
  setLanguage: (language: Language) => void;
  themePreference: ThemePreference;
  setThemePreference: (theme: ThemePreference) => void;
  resolvedTheme: ResolvedTheme;
  t: (key: TranslationKey) => string;
  formatDate: (value: string | Date) => string;
}

const LANGUAGE_KEY = 'ui_language';
const THEME_KEY = 'ui_theme';

const getStoredLanguage = (): Language => {
  const stored = window.localStorage.getItem(LANGUAGE_KEY);
  return stored === 'pl' ? 'pl' : 'en';
};

const getStoredTheme = (): ThemePreference => {
  const stored = window.localStorage.getItem(THEME_KEY);
  if (stored === 'light' || stored === 'dark' || stored === 'system') {
    return stored;
  }
  return 'system';
};

const getSystemTheme = (): ResolvedTheme =>
  window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';

const defaultContext: UISettingsContextType = {
  language: 'en',
  setLanguage: () => undefined,
  themePreference: 'system',
  setThemePreference: () => undefined,
  resolvedTheme: 'light',
  t: (key) => translations.en[key],
  formatDate: (value) =>
    new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric' }).format(
      typeof value === 'string' ? new Date(value) : value
    ),
};

const UISettingsContext = createContext<UISettingsContextType>(defaultContext);

export const UISettingsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [language, setLanguage] = useState<Language>(() => getStoredLanguage());
  const [themePreference, setThemePreference] = useState<ThemePreference>(() => getStoredTheme());
  const [systemTheme, setSystemTheme] = useState<ResolvedTheme>(() => getSystemTheme());

  useEffect(() => {
    const media = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = () => {
      setSystemTheme(media.matches ? 'dark' : 'light');
    };

    handleChange();
    media.addEventListener('change', handleChange);
    return () => media.removeEventListener('change', handleChange);
  }, []);

  useEffect(() => {
    window.localStorage.setItem(LANGUAGE_KEY, language);
  }, [language]);

  useEffect(() => {
    window.localStorage.setItem(THEME_KEY, themePreference);
  }, [themePreference]);

  const resolvedTheme = themePreference === 'system' ? systemTheme : themePreference;

  useEffect(() => {
    const root = document.documentElement;
    const body = document.body;
    root.classList.toggle('dark', resolvedTheme === 'dark');
    body.classList.toggle('dark', resolvedTheme === 'dark');
    root.dataset.theme = resolvedTheme;
  }, [resolvedTheme]);

  const value = useMemo<UISettingsContextType>(() => {
    const locale = language === 'pl' ? 'pl-PL' : 'en-US';
    return {
      language,
      setLanguage,
      themePreference,
      setThemePreference,
      resolvedTheme,
      t: (key) => translations[language][key],
      formatDate: (value) =>
        new Intl.DateTimeFormat(locale, { month: 'short', day: 'numeric', year: 'numeric' }).format(
          typeof value === 'string' ? new Date(value) : value
        ),
    };
  }, [language, themePreference, resolvedTheme]);

  return <UISettingsContext.Provider value={value}>{children}</UISettingsContext.Provider>;
};

export const useUISettings = () => useContext(UISettingsContext);
